import React, { useEffect, useState } from "react";
import { Plus, Building2, Users, QrCode, UtensilsCrossed, Pause, Play, Loader2, ChevronDown, ChevronUp, Trash2, Save } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { api } from "../api";
import { useToast } from "../hooks/use-toast";
import StripeConfigCard from "./StripeConfigCard";

const PLANS = ["free", "qr", "minisite", "menu", "complete"];
const SERVICES = [["pages", "Qora Link"], ["qr", "Qora QR Pro"], ["minisite", "Qora MiniSite"], ["menus", "Qora Menu"]];
const EMPTY = { name: "", owner_name: "", owner_email: "", owner_password: "", plan: "free" };

const validate = (f) => {
  const e = {};
  if (f.name.trim().length < 2) e.name = "Inserisci il nome dell'attività (min 2 caratteri)";
  if (f.owner_name.trim().length < 2) e.owner_name = "Inserisci il nome del proprietario";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.owner_email.trim())) e.owner_email = "Email non valida";
  if (f.owner_password.length < 6) e.owner_password = "La password deve avere almeno 6 caratteri";
  return e;
};

export default function AdminPanel() {
  const { toast } = useToast();
  const [stats, setStats] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [loadError, setLoadError] = useState("");
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(null);
  const [q, setQ] = useState("");

  const load = async () => {
    try { const [s, c] = await Promise.all([api.adminStats(), api.adminCompanies()]); setStats(s.data); setCompanies(c.data); setLoadError(""); }
    catch (e) { setLoadError(e?.response?.data?.detail || "Caricamento non riuscito"); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    const errs = validate(form);
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setBusy(true);
    try {
      const { data } = await api.adminCreateCompany({ ...form, name: form.name.trim(), owner_name: form.owner_name.trim(), owner_email: form.owner_email.trim() });
      toast({ title: "Azienda creata", description: `${form.name} · accesso: ${form.owner_email}` });
      setCreating(false); setForm(EMPTY); setErrors({});
      await load(); setOpen(data.company_id);
    } catch (e) { toast({ title: "Errore nella creazione", description: e?.response?.data?.detail, variant: "destructive" }); }
    finally { setBusy(false); }
  };

  const toggleStatus = async (c) => {
    try {
      await api.adminUpdateCompany(c.id, { status: c.status === "active" ? "suspended" : "active" });
      toast({ title: c.status === "active" ? "Azienda sospesa" : "Azienda riattivata" });
      load();
    } catch (e) { toast({ title: "Errore", description: e?.response?.data?.detail, variant: "destructive" }); }
  };

  const cards = stats ? [
    { label: "Aziende", value: stats.companies, icon: Building2 },
    { label: "Utenti", value: stats.users, icon: Users },
    { label: "QR Code", value: stats.qrcodes, icon: QrCode },
    { label: "Scansioni", value: stats.scans, icon: UtensilsCrossed },
  ] : [];
  const list = companies.filter((c) => !q || c.name.toLowerCase().includes(q.toLowerCase()) || (c.owner_email || "").includes(q.toLowerCase()));

  return (
    <div data-testid="admin-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div><h1 className="font-display text-2xl md:text-3xl font-extrabold">Amministrazione Qora</h1><p className="text-muted-foreground mt-1">Gestisci aziende, piani, servizi e stato degli account.</p></div>
        <Button className="btn-grad text-white font-semibold gap-2" onClick={() => { setErrors({}); setCreating(true); }} data-testid="admin-new-company-btn"><Plus className="h-4 w-4" /> Nuova azienda</Button>
      </div>

      {loadError && <div className="rounded-xl border border-rose-200 bg-rose-50 text-rose-800 text-sm p-3 mb-4 flex items-center justify-between" data-testid="admin-load-error">{loadError}<Button size="sm" variant="outline" onClick={load}>Riprova</Button></div>}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {cards.map((c) => { const Icon = c.icon; return (
          <div key={c.label} className="rounded-2xl border border-border bg-white p-5 card-shadow" data-testid={`admin-stat-${c.label.toLowerCase()}`}>
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/8 text-primary mb-3"><Icon className="h-5 w-5" /></span>
            <p className="font-display text-2xl font-extrabold">{c.value}</p>
            <p className="text-sm text-muted-foreground">{c.label}</p>
          </div>); })}
      </div>

      <StripeConfigCard />

      <div className="rounded-2xl border border-border bg-white card-shadow overflow-hidden">
        <div className="p-4 sm:p-5 border-b border-border flex flex-wrap items-center justify-between gap-3"><h3 className="font-semibold">Aziende ({companies.length})</h3><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per nome o email…" className="h-9 max-w-xs" data-testid="admin-search" /></div>
        <div className="divide-y divide-border">
          {list.map((c) => (
            <div key={c.id} data-testid={`admin-company-${c.id}`}>
              <div className="flex items-center gap-3 sm:gap-4 p-4">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-muted text-foreground font-bold shrink-0">{(c.name || "?")[0]}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-semibold truncate" data-testid="admin-company-name">{c.name}</p>
                    <Badge variant="secondary" className="text-[10px]">{c.plan}</Badge>
                    <Badge variant={c.status === "active" ? "secondary" : "outline"} className={c.status === "active" ? "bg-emerald-50 text-emerald-600 text-[10px]" : "text-destructive text-[10px]"} data-testid="admin-company-status">{c.status === "active" ? "Attiva" : "Sospesa"}</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5 truncate">{c.owner_email || "—"} · {c.users} utenti · {c.qr_count} QR · {c.page_count} pagine · {c.menu_count} menù</p>
                </div>
                <Button size="sm" variant="outline" className="gap-1.5 hidden sm:inline-flex" onClick={() => toggleStatus(c)} data-testid="admin-company-toggle">
                  {c.status === "active" ? <><Pause className="h-3.5 w-3.5" /> Sospendi</> : <><Play className="h-3.5 w-3.5" /> Riattiva</>}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setOpen(open === c.id ? null : c.id)} aria-label="Dettagli" data-testid="admin-company-open">{open === c.id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}</Button>
              </div>
              {open === c.id && <CompanyDetail c={c} onChange={load} onToggle={() => toggleStatus(c)} />}
            </div>
          ))}
          {!list.length && <p className="p-6 text-sm text-muted-foreground text-center">Nessuna azienda trovata.</p>}
        </div>
      </div>

      <Dialog open={creating} onOpenChange={(v) => { if (!busy) setCreating(v); }}>
        <DialogContent className="max-w-sm" data-testid="admin-create-dialog">
          <DialogHeader><DialogTitle>Nuova azienda</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {[["name", "Nome azienda", "text", "Es. Trattoria Da Marco"], ["owner_name", "Nome proprietario", "text", "Es. Marco Rossi"], ["owner_email", "Email proprietario", "email", "marco@esempio.it"], ["owner_password", "Password iniziale", "text", "min 6 caratteri"]].map(([k, l, t, ph]) => (
              <div key={k}><Label className="text-xs text-muted-foreground">{l}</Label><Input type={t} value={form[k]} onChange={(e) => { setForm({ ...form, [k]: e.target.value }); if (errors[k]) setErrors({ ...errors, [k]: undefined }); }} className={`h-10 mt-1 ${errors[k] ? "border-rose-400" : ""}`} placeholder={ph} data-testid={`admin-create-${k}`} />{errors[k] && <p className="text-xs text-rose-600 mt-1" data-testid={`admin-create-error-${k}`}>{errors[k]}</p>}</div>
            ))}
            <div><Label className="text-xs text-muted-foreground">Piano (etichetta)</Label><select value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })} className="h-10 mt-1 w-full rounded-md border border-input bg-background px-3 text-sm" data-testid="admin-create-plan">{PLANS.map((p) => <option key={p} value={p}>{p}</option>)}</select><p className="text-[11px] text-muted-foreground mt-1">I servizi premium si attivano dopo la creazione, dalla scheda azienda.</p></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setCreating(false)} disabled={busy}>Annulla</Button><Button className="btn-grad text-white font-semibold" onClick={create} disabled={busy} data-testid="admin-create-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Crea azienda"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CompanyDetail({ c, onChange, onToggle }) {
  const { toast } = useToast();
  const [edit, setEdit] = useState({ name: c.name, plan: c.plan, ai_monthly_limit: c.ai_monthly_limit ?? 200 });
  const [busy, setBusy] = useState("");
  const [confirmDel, setConfirmDel] = useState(false);
  useEffect(() => setEdit({ name: c.name, plan: c.plan, ai_monthly_limit: c.ai_monthly_limit ?? 200 }), [c.id, c.name, c.plan, c.ai_monthly_limit]);

  const save = async () => {
    if (edit.name.trim().length < 2) return toast({ title: "Nome non valido", description: "Minimo 2 caratteri", variant: "destructive" });
    setBusy("save");
    try { await api.adminUpdateCompany(c.id, { name: edit.name.trim(), plan: edit.plan, ai_monthly_limit: Number(edit.ai_monthly_limit) || 0 }); toast({ title: "Azienda aggiornata" }); onChange(); }
    catch (e) { toast({ title: "Errore", description: e?.response?.data?.detail, variant: "destructive" }); }
    finally { setBusy(""); }
  };
  const toggleService = async (svc, on) => {
    setBusy(svc);
    try {
      if (on) await api.adminGrant({ company_id: c.id, services: [svc] }); else await api.adminRevoke({ company_id: c.id, service: svc });
      toast({ title: on ? "Servizio attivato" : "Servizio disattivato", description: SERVICES.find((s) => s[0] === svc)?.[1] });
      onChange();
    } catch (e) { toast({ title: "Errore", description: e?.response?.data?.detail, variant: "destructive" }); }
    finally { setBusy(""); }
  };
  const del = async () => {
    setBusy("del");
    try { await api.adminDeleteCompany(c.id); toast({ title: "Azienda eliminata", description: c.name }); setConfirmDel(false); onChange(); }
    catch (e) { toast({ title: "Errore", description: e?.response?.data?.detail, variant: "destructive" }); }
    finally { setBusy(""); }
  };
  const svc = c.services || {};

  return (
    <div className="px-4 pb-4 sm:px-5 grid lg:grid-cols-2 gap-4" data-testid="admin-company-detail">
      <div className="rounded-xl border border-border p-4 space-y-3">
        <p className="text-sm font-semibold">Dati azienda</p>
        <div><Label className="text-xs text-muted-foreground">Nome</Label><Input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} className="h-9 mt-1" data-testid="admin-edit-name" /></div>
        <div className="grid grid-cols-2 gap-2">
          <div><Label className="text-xs text-muted-foreground">Piano</Label><select value={PLANS.includes(edit.plan) ? edit.plan : "free"} onChange={(e) => setEdit({ ...edit, plan: e.target.value })} className="h-9 mt-1 w-full rounded-md border border-input bg-background px-2 text-sm" data-testid="admin-edit-plan">{PLANS.map((p) => <option key={p} value={p}>{p}</option>)}</select></div>
          <div><Label className="text-xs text-muted-foreground">Limite AI / mese</Label><Input type="number" value={edit.ai_monthly_limit} onChange={(e) => setEdit({ ...edit, ai_monthly_limit: e.target.value })} className="h-9 mt-1" data-testid="admin-edit-ai" /></div>
        </div>
        <p className="text-xs text-muted-foreground">Proprietario: <b>{c.owner_name || "—"}</b> · {c.owner_email || "—"} · AI usata {c.ai_used ?? 0}/{c.ai_monthly_limit ?? 0} · creata {c.created_at ? new Date(c.created_at).toLocaleDateString("it-IT") : "—"}</p>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" className="gap-1.5 bg-[#0c2d34] hover:bg-[#0c2d34]/90 text-white" onClick={save} disabled={!!busy} data-testid="admin-edit-save">{busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Salva</Button>
          <Button size="sm" variant="outline" className="gap-1.5 sm:hidden" onClick={onToggle}>{c.status === "active" ? <><Pause className="h-3.5 w-3.5" /> Sospendi</> : <><Play className="h-3.5 w-3.5" /> Riattiva</>}</Button>
          <Button size="sm" variant="outline" className="gap-1.5 text-destructive ml-auto" onClick={() => setConfirmDel(true)} data-testid="admin-delete-btn"><Trash2 className="h-3.5 w-3.5" /> Elimina</Button>
        </div>
      </div>
      <div className="rounded-xl border border-border p-4">
        <p className="text-sm font-semibold">Servizi attivi (grant manuale)</p>
        <p className="text-xs text-muted-foreground mb-3">Attiva o disattiva i moduli senza passare da Stripe. Gli abbonamenti Stripe restano gestiti dal cliente.</p>
        <div className="space-y-2">
          {SERVICES.map(([k, l]) => { const s = svc[k] || {}; return (
            <div key={k} className="flex items-center gap-3 rounded-lg bg-muted/40 px-3 py-2" data-testid={`admin-service-${k}`}>
              <div className="flex-1 min-w-0"><p className="text-sm font-medium">{l}</p><p className="text-[11px] text-muted-foreground truncate">{s.active ? `attivo · ${s.source || "—"}${s.expires_at ? ` · scade ${new Date(s.expires_at).toLocaleDateString("it-IT")}` : ""}` : "non attivo"}</p></div>
              {busy === k ? <Loader2 className="h-4 w-4 animate-spin" /> : <Switch checked={!!s.active} onCheckedChange={(v) => toggleService(k, v)} data-testid={`admin-service-toggle-${k}`} />}
            </div>); })}
        </div>
      </div>
      <Dialog open={confirmDel} onOpenChange={setConfirmDel}>
        <DialogContent className="max-w-sm" data-testid="admin-delete-dialog">
          <DialogHeader><DialogTitle>Eliminare «{c.name}»?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Verranno eliminati utenti, QR, pagine, menù e statistiche dell'azienda. L'operazione non è reversibile.</p>
          <DialogFooter><Button variant="outline" onClick={() => setConfirmDel(false)}>Annulla</Button><Button variant="destructive" onClick={del} disabled={busy === "del"} data-testid="admin-delete-confirm">{busy === "del" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Elimina definitivamente"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
